import React, { memo, type ReactElement, type ReactNode } from 'react';
import { Avatar, Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_SEC_BLD, testIds } from '../../constants';

import './SodUserCard.css';

/**
 * Props interface for the SodUserCard component
 */
interface SodUserCardProps {
  firstName: string | undefined;
  lastName: string | undefined;
  userName: string | undefined;
  link?: string;
  trailContent?: ReactNode;
  className?: string;
  avatarSize?: 'xxSmall' | 'xSmall' | 'small' | 'medium' | 'large' | 'xLarge';
}

/**
 * SodUserCard
 *
 * A card component that displays user information for SOD (Separation of Duties)
 *
 * @typedef {Object} SodUserCardProps
 * @property {string} [userName] - The name of the user to display
 * @property {string} [avatarUrl] - URL for the user's avatar/profile picture
 * @property {React.ReactNode} [children] - Optional child elements to render within the card
 *
 * @example
 * ```tsx
 * <SodUserCard
 *   userName="John Doe"
 *   userEmail="john.doe@example.com"
 *   userRole="Administrator"
 *   avatarUrl="/path/to/avatar.jpg"
 * >
 *   <AdditionalContent />
 * </SodUserCard>
 * ```
 */
function SodUserCard({
  firstName,
  lastName,
  userName,
  link,
  trailContent,
  className,
  avatarSize = 'small',
}: SodUserCardProps): ReactElement | null {
  const wrapperClasses = cx('Sod-UserCard', className);

  const isFirstName = Boolean(firstName && firstName.trim().length !== 0);
  const isLastName = Boolean(lastName && lastName.trim().length !== 0);
  const isUserName = Boolean(userName && userName.trim().length !== 0);

  if (!isFirstName && !isLastName && !isUserName) {
    return null;
  }

  if (!isFirstName && !isLastName) {
    return (
      <Box className={wrapperClasses} dataTestId={testIds.SOD_USER_CARD}>
        <Avatar size={avatarSize} />
        {trailContent ?? (
          <Typography
            color={CF_SEC_BLD}
            kind='body2-bold'
            className='Sod-UserCard-text'>
            {userName}
          </Typography>
        )}
      </Box>
    );
  }

  const tempFirstName = firstName ?? 'Not ';
  const tempLastName = lastName ?? 'Available';

  const tempUserName = `${tempFirstName} ${tempLastName}`;
  const initials = `${tempFirstName[0].toUpperCase()}${tempLastName[0].toUpperCase()}`;

  return (
    <Box className={wrapperClasses} dataTestId={testIds.SOD_USER_CARD}>
      {link ? (
        <Avatar
          alt={userName}
          size={avatarSize}
          src={link}
          className='Sod-UserCard-avatar'
        />
      ) : (
        <Avatar size={avatarSize} className='Sod-UserCard-avatar'>
          {initials}
        </Avatar>
      )}
      {trailContent ?? (
        <Typography
          kind='body2-bold'
          color={CF_SEC_BLD}
          className='Sod-UserCard-text'>
          {tempUserName}
        </Typography>
      )}
    </Box>
  );
}

SodUserCard.defaultProps = {
  link: null,
  trailContent: null,
  className: null,
  avatarSize: null,
};

export default memo(SodUserCard);
